import { useEffect, useState } from 'react'

const SCRIPT_SRC = 'https://js.chargebee.com/v2/chargebee.js'
const SCRIPT_ID = 'chargebee-js-sdk'

type CbCheckoutCallbacks = {
  loaded?: () => void
  success?: (data: { id: string }) => void
  close?: () => void
  step?: (step: string) => void
  error?: (err: unknown) => void
  resize?: (size: { height: number; width: number } | number) => void
}

export type CbCheckout = {
  mount: (containerSelector: string | HTMLElement) => void
}

export type CbInstance = {
  createCheckout: (opts: {
    url?: string
    callbacks?: CbCheckoutCallbacks
  }) => Promise<CbCheckout>
}

type CbGlobal = {
  init: (opts: { site: string }) => CbInstance
}

declare global {
  interface Window {
    Chargebee?: CbGlobal
  }
}

export type ChargebeeSdkStatus = 'loading' | 'ready' | 'error'

type State = {
  status: ChargebeeSdkStatus
  cbInstance?: CbInstance
}

let modulePromise: Promise<void> | null = null
let moduleInstance: CbInstance | undefined
let subscribers: Array<(s: State) => void> = []
let currentState: State = { status: 'loading' }

function setState(next: State): void {
  if (
    next.status === currentState.status &&
    next.cbInstance === currentState.cbInstance
  ) {
    return
  }
  currentState = next
  subscribers.forEach((fn) => fn(next))
}

function loadScript(): Promise<void> {
  if (modulePromise) return modulePromise
  modulePromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      if (window.Chargebee) {
        resolve()
      } else {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () => reject(new Error('load')))
      }
      return
    }
    const tag = document.createElement('script')
    tag.id = SCRIPT_ID
    tag.src = SCRIPT_SRC
    tag.async = true
    tag.onload = (): void => resolve()
    tag.onerror = (): void => reject(new Error('load'))
    document.head.appendChild(tag)
  })
  return modulePromise
}

export function __resetForTests(): void {
  modulePromise = null
  moduleInstance = undefined
  subscribers = []
  currentState = { status: 'loading' }
}

export default function useChargebeeSdk(cfg: { site: string }): State {
  const [state, setLocal] = useState<State>(currentState)

  useEffect(() => {
    subscribers.push(setLocal)
    setLocal(currentState)

    loadScript()
      .then(() => {
        if (!window.Chargebee) {
          setState({ status: 'error' })
          return
        }
        if (!moduleInstance) {
          // TODO(backend): Chargebee's SDK redirects to the plan's
          // redirect_url after checkout success, even with
          // enableTopLevelRedirectOnSuccess:false. The redirect is
          // performed by chargebee.js in our window context, so we
          // can't block it from the frontend. The backend needs to
          // either omit redirect_url when creating hosted pages for
          // embedded checkout, or the redirect URL should be removed
          // from the plan configuration in the Chargebee dashboard.
          moduleInstance = window.Chargebee.init({
            site: cfg.site,
          })
        }
        setState({ status: 'ready', cbInstance: moduleInstance })
      })
      .catch(() => setState({ status: 'error' }))

    return () => {
      subscribers = subscribers.filter((fn) => fn !== setLocal)
    }
  }, [cfg.site])

  return state
}
